import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const ADMIN_KEY = "tdqNfozEyFKfXIPU95cO38w102WC9MWz_nXOl9ZJV2M";
const LICENCE_API = "https://licence.golfsocietymanager.com/api";

export default function LicenceDashboard() {
  const navigate = useNavigate();
  const token = sessionStorage.getItem("token");
  const [licences, setLicences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [events, setEvents] = useState([]);
  const [toast, setToast] = useState("");
  const [extendDays, setExtendDays] = useState(14);
  const [activateMonths, setActivateMonths] = useState(12);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 3000); };

  useEffect(() => {
    if (!token) { navigate("/superadmin"); return; }
    loadLicences();
  }, []);

  const loadLicences = async () => {
    try {
      const res = await axios.get(LICENCE_API + "/admin/licences", { headers: { "x-admin-key": ADMIN_KEY } });
      setLicences(res.data);
    } catch(e) { setError("Failed to load licences — check licence server is running"); }
    finally { setLoading(false); }
  };

  const loadEvents = async (key) => {
    try {
      const res = await axios.get(LICENCE_API + "/admin/events/" + key, { headers: { "x-admin-key": ADMIN_KEY } });
      setEvents(res.data);
    } catch(e) { setEvents([]); }
  };

  const selectLicence = (lic) => { setSelected(lic); loadEvents(lic.licence_key); };

  const extendTrial = async (key) => {
    try {
      await axios.post(LICENCE_API + "/admin/extend-trial", { licence_key: key, days: extendDays }, { headers: { "x-admin-key": ADMIN_KEY } });
      showToast(`Trial extended by ${extendDays} days`);
      loadLicences();
    } catch(e) { showToast("Failed to extend trial"); }
  };

  const activate = async (key) => {
    try {
      await axios.post(LICENCE_API + "/admin/activate", { licence_key: key, months: activateMonths }, { headers: { "x-admin-key": ADMIN_KEY } });
      showToast(`Activated for ${activateMonths} months`);
      loadLicences();
    } catch(e) { showToast("Failed to activate"); }
  };

  const suspend = async (key) => {
    if (!window.confirm("Suspend this licence?")) return;
    try {
      await axios.post(LICENCE_API + "/admin/suspend", { licence_key: key, reason: "Admin suspended" }, { headers: { "x-admin-key": ADMIN_KEY } });
      showToast("Licence suspended"); loadLicences();
    } catch(e) { showToast("Failed to suspend"); }
  };

  const deleteLicence = async (key) => {
    if (!window.confirm("Delete this licence permanently?")) return;
    try {
      await axios.delete(LICENCE_API + "/admin/licence/" + key, { headers: { "x-admin-key": ADMIN_KEY } });
      showToast("Licence deleted");
      setLicences(prev => prev.filter(l => l.licence_key !== key));
      if (selected?.licence_key === key) setSelected(null);
    } catch(e) { showToast("Failed to delete"); }
  };

  const statusColor = (s) => ({ active:"#166534", trial:"#1d4ed8", expired:"#b91c1c", suspended:"#7c3aed" }[s] || "#6b7280");
  const filtered = licences.filter(l => [l.society_name, l.email, l.licence_key].join(" ").toLowerCase().includes(search.toLowerCase()));
  const stats = { total: licences.length, trial: licences.filter(l=>l.status==='trial').length, active: licences.filter(l=>l.status==='active').length, expired: licences.filter(l=>l.status==='expired').length, suspended: licences.filter(l=>l.status==='suspended').length };

  return (
    <div style={{ minHeight:"100vh", background:"#1e1b4b" }}>
      {toast && <div style={{ position:"fixed", bottom:"20px", right:"20px", background:"#166534", color:"white", padding:"10px 20px", borderRadius:"8px", zIndex:9999, fontWeight:"600" }}>{toast}</div>}
      <div style={{ background:"#312e81", padding:"14px 24px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <div style={{ display:"flex", alignItems:"center", gap:"12px" }}>
          <span style={{ fontSize:"22px" }}>🔑</span>
          <div>
            <div style={{ color:"white", fontWeight:"800", fontSize:"15px" }}>GSM Licence Manager</div>
            <div style={{ color:"#a5b4fc", fontSize:"11px" }}>Offline Desktop App activation codes only — not the website or Android</div>
          </div>
        </div>
        <div style={{ display:"flex", gap:"10px" }}>
          <button onClick={() => navigate("/superadmin/dashboard")} style={{ background:"none", border:"1px solid #4f46e5", color:"#a5b4fc", borderRadius:"6px", padding:"6px 14px", fontSize:"12px", cursor:"pointer" }}>← Societies</button>
          <button onClick={() => { sessionStorage.removeItem("token"); navigate("/superadmin"); }} style={{ background:"#dc2626", color:"white", border:"none", borderRadius:"6px", padding:"6px 14px", fontSize:"12px", fontWeight:"700", cursor:"pointer" }}>Logout</button>
        </div>
      </div>

      <div style={{ padding:"20px", maxWidth:"1200px", margin:"0 auto" }}>
        <div style={{ color:"#a5b4fc", fontSize:"12px", marginBottom:"12px" }}>
          Each row below is one activation code for the Windows desktop app — created automatically the first time it's installed and run on a machine. This has no connection to how many times the installer has been downloaded.
        </div>
        <div style={{ display:"flex", gap:"12px", marginBottom:"20px", flexWrap:"wrap" }}>
          {[["TOTAL",stats.total,"#312e81"],["TRIAL",stats.trial,"#1e40af"],["ACTIVE",stats.active,"#166534"],["EXPIRED",stats.expired,"#991b1b"],["SUSPENDED",stats.suspended,"#5b21b6"]].map(([label,val,bg]) => (
            <div key={label} style={{ background:bg, borderRadius:"10px", padding:"14px 20px", minWidth:"100px", textAlign:"center" }}>
              <div style={{ color:"#c7d2fe", fontSize:"10px", fontWeight:"600", marginBottom:"4px" }}>{label}</div>
              <div style={{ color:"white", fontSize:"26px", fontWeight:"800" }}>{val}</div>
            </div>
          ))}
        </div>

        <div style={{ display:"flex", gap:"16px" }}>
          <div style={{ flex:1, minWidth:0 }}>
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search society, email or licence key..."
              style={{ width:"100%", border:"1px solid #4f46e5", background:"#312e81", color:"white", borderRadius:"8px", padding:"10px 14px", fontSize:"14px", marginBottom:"12px", boxSizing:"border-box" }} />
            {loading && <div style={{ color:"#a5b4fc", textAlign:"center", padding:"30px" }}>Loading...</div>}
            {error && <div style={{ color:"#f87171", padding:"10px" }}>{error}</div>}
            <div style={{ display:"flex", flexDirection:"column", gap:"8px" }}>
              {filtered.map(l => (
                <div key={l.licence_key} onClick={() => selectLicence(l)}
                  style={{ background: selected?.licence_key===l.licence_key ? "#3730a3" : "#312e81", borderRadius:"10px", padding:"14px 16px", cursor:"pointer", border: selected?.licence_key===l.licence_key ? "1px solid #818cf8" : "1px solid transparent" }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                    <div>
                      <div style={{ color:"white", fontWeight:"700", fontSize:"14px" }}>{l.society_name}</div>
                      <div style={{ color:"#a5b4fc", fontSize:"12px", marginTop:"2px" }}>{l.email}</div>
                      <div style={{ color:"#6b7280", fontSize:"11px", marginTop:"4px", fontFamily:"monospace" }}>{l.licence_key}</div>
                    </div>
                    <div style={{ textAlign:"right" }}>
                      <span style={{ background:statusColor(l.status), color:"white", fontSize:"11px", fontWeight:"700", padding:"2px 10px", borderRadius:"20px" }}>{l.status?.toUpperCase()}</span>
                      {l.status==='trial' && <div style={{ color:"#fbbf24", fontSize:"11px", marginTop:"4px" }}>{Math.max(0,Math.ceil(l.trial_days_left))} days left</div>}
                    </div>
                  </div>
                </div>
              ))}
              {!loading && filtered.length===0 && <div style={{ color:"#a5b4fc", textAlign:"center", padding:"20px" }}>No licences found</div>}
            </div>
          </div>

          {selected && (
            <div style={{ width:"340px", flexShrink:0 }}>
              <div style={{ background:"#312e81", borderRadius:"10px", padding:"20px", marginBottom:"12px" }}>
                <div style={{ color:"white", fontWeight:"800", fontSize:"15px", marginBottom:"4px" }}>{selected.society_name}</div>
                <div style={{ color:"#a5b4fc", fontSize:"13px", marginBottom:"2px" }}>{selected.email}</div>
                <div style={{ color:"#6b7280", fontSize:"11px", fontFamily:"monospace", marginBottom:"12px" }}>{selected.licence_key}</div>
                <div style={{ background:"#1e1b4b", borderRadius:"8px", padding:"12px", marginBottom:"16px", fontSize:"12px" }}>
                  {[["Status", selected.status?.toUpperCase()], selected.trial_end && ["Trial ends", new Date(selected.trial_end).toLocaleDateString('en-GB')], selected.subscription_end && ["Sub ends", new Date(selected.subscription_end).toLocaleDateString('en-GB')], selected.last_check && ["Last seen", new Date(selected.last_check).toLocaleDateString('en-GB')]].filter(Boolean).map(([k,v]) => (
                    <div key={k} style={{ display:"flex", justifyContent:"space-between", marginBottom:"4px" }}>
                      <span style={{ color:"#a5b4fc" }}>{k}</span>
                      <span style={{ color:"white", fontWeight:"600" }}>{v}</span>
                    </div>
                  ))}
                </div>
                <div style={{ marginBottom:"10px" }}>
                  <div style={{ color:"#c7d2fe", fontSize:"11px", fontWeight:"600", marginBottom:"6px" }}>EXTEND TRIAL</div>
                  <div style={{ display:"flex", gap:"8px" }}>
                    <input type="number" value={extendDays} onChange={e => setExtendDays(e.target.value)} style={{ width:"60px", background:"#1e1b4b", border:"1px solid #4f46e5", color:"white", borderRadius:"6px", padding:"6px", fontSize:"13px" }} />
                    <span style={{ color:"#a5b4fc", fontSize:"12px", alignSelf:"center" }}>days</span>
                    <button onClick={() => extendTrial(selected.licence_key)} style={{ flex:1, background:"#1d4ed8", color:"white", border:"none", borderRadius:"6px", padding:"6px 12px", fontSize:"12px", fontWeight:"700", cursor:"pointer" }}>Extend</button>
                  </div>
                </div>
                <div style={{ marginBottom:"10px" }}>
                  <div style={{ color:"#c7d2fe", fontSize:"11px", fontWeight:"600", marginBottom:"6px" }}>ACTIVATE SUBSCRIPTION</div>
                  <div style={{ display:"flex", gap:"8px" }}>
                    <input type="number" value={activateMonths} onChange={e => setActivateMonths(e.target.value)} style={{ width:"60px", background:"#1e1b4b", border:"1px solid #4f46e5", color:"white", borderRadius:"6px", padding:"6px", fontSize:"13px" }} />
                    <span style={{ color:"#a5b4fc", fontSize:"12px", alignSelf:"center" }}>months</span>
                    <button onClick={() => activate(selected.licence_key)} style={{ flex:1, background:"#166534", color:"white", border:"none", borderRadius:"6px", padding:"6px 12px", fontSize:"12px", fontWeight:"700", cursor:"pointer" }}>Activate</button>
                  </div>
                </div>
                <div style={{ display:"flex", gap:"8px" }}>
                  <button onClick={() => suspend(selected.licence_key)} style={{ flex:1, background:"#5b21b6", color:"white", border:"none", borderRadius:"6px", padding:"8px", fontSize:"12px", fontWeight:"700", cursor:"pointer" }}>Suspend</button>
                  <button onClick={() => deleteLicence(selected.licence_key)} style={{ flex:1, background:"#991b1b", color:"white", border:"none", borderRadius:"6px", padding:"8px", fontSize:"12px", fontWeight:"700", cursor:"pointer" }}>Delete</button>
                </div>
              </div>
              <div style={{ background:"#312e81", borderRadius:"10px", padding:"16px" }}>
                <div style={{ color:"#c7d2fe", fontSize:"11px", fontWeight:"600", marginBottom:"10px" }}>EVENT LOG</div>
                {events.length===0 && <div style={{ color:"#6b7280", fontSize:"12px" }}>No events</div>}
                {events.map(ev => (
                  <div key={ev.id} style={{ borderBottom:"1px solid #1e1b4b", paddingBottom:"8px", marginBottom:"8px" }}>
                    <div style={{ display:"flex", justifyContent:"space-between" }}>
                      <span style={{ color:"white", fontSize:"12px", fontWeight:"600" }}>{ev.event_type}</span>
                      <span style={{ color:"#6b7280", fontSize:"11px" }}>{new Date(ev.created_at).toLocaleDateString('en-GB')}</span>
                    </div>
                    {ev.detail && <div style={{ color:"#a5b4fc", fontSize:"11px", marginTop:"2px" }}>{ev.detail}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
